export const SERVICE_FIELDS = {
  Exam: ['dilationPerformed', 'isMemberDiabetic', 'diabeticCodes', 'diagnosisCodes', 'procedureCodes'],
  Frames: ['diagnosisCodes', 'procedureCodes'],
  'Spectacle Lens': ['spectacle_lens'],
  'Contact Lens': ['diagnosisCodes', 'procedureCodes'],
  'Contact Lens Fit & F/U': ['diagnosisCodes', 'procedureCodes'],
}

export const DISEASE_NONE = {
  codeID: '33448',
  code: 'NONE',
  codeType: 'EXAM_PRCDR_DISEASE_CD',
  description: 'None',
  isDiabetic: false,
  readOnlyCptCodes: false,
  disabled: false,
}

export const ADDITIONAL_NONE = {
  codeID: '33423',
  code: 'NONE',
  codeType: 'NON_DBTC_ADTNL_COND_LSTG',
  description: 'None',
  isDiabetic: false,
  readOnlyCptCodes: false,
}

export const formatVersantCodeLabel = (code = {}) => `${code.code || ''} - ${code.description || ''}`.trim()

export const normalizeVersantCodes = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value.filter((code) => code?.code)
  return value?.code ? [value] : []
}

export const formatVersantCodesLabel = (codes = []) => (
  normalizeVersantCodes(codes).map(formatVersantCodeLabel).filter(Boolean).join(', ')
)

export const mapVersantCodeToOption = (item) => ({
  ...item,
  label: formatVersantCodeLabel(item),
})

export const formatRegisteredLabLabel = (lab = {}) => `${lab.labName || ''} - ${lab.account_Number || ''}`.trim()

export const mapRegisteredLabToOption = (lab) => {
  const label = formatRegisteredLabLabel(lab)
  return {
    name: label,
    value: label,
    ...lab,
  }
}

export const findRegisteredLabByValue = (labs = [], value) => (
  labs.find((lab) => {
    const label = formatRegisteredLabLabel(lab)
    return String(lab.lab_SKey || lab.account_Number) === String(value) || label === value
  })
)

export const mapLenDesignRows = (designs = []) => (
  designs.flatMap((design) => (design.materials || []).map((material) => ({
    id: material.designMaterialId,
    lensMaterial: material.lensMaterialName,
    lensDesign: design.lensDesignName,
    lensTier: design.tier,
    designCode: design.designCode,
    materialCode: material.materialCode,
    procedureCodes: material.procedureCodes || [],
  })))
)

const LENS_TYPE_API = {
  single_vision: 'Single Vision',
  bi_focal: 'Bi Focal',
  tri_focal: 'Tri Focal',
  progressive: 'Progressive',
  Single: 'Single Vision',
  'Bi Focal': 'Bi Focal',
  'Tri Focal': 'Tri Focal',
  Progressive: 'Progressive',
}

const RANGE_IDS_BY_LENS_TYPE = {
  bi_focal: ['BF0001', 'BF0001', 'BF0002', 'BF0003'],
  tri_focal: ['BF0001', 'BF0001', 'BF0002', 'BF0003', 'PG0002', 'PG0004', 'PG0102'],
  progressive: ['PG0002', 'PG0004', 'PG0102'],
  'Bi Focal': ['BF0001', 'BF0001', 'BF0002', 'BF0003'],
  'Tri Focal': ['BF0001', 'BF0001', 'BF0002', 'BF0003', 'PG0002', 'PG0004', 'PG0102'],
  Progressive: ['PG0002', 'PG0004', 'PG0102'],
}

export const getLenDesignLensType = (lensType) => LENS_TYPE_API[lensType] || lensType

export const getLenDesignRangeIds = (lensType) => RANGE_IDS_BY_LENS_TYPE[lensType] || []

export const getTreatmentLabel = (item = {}) => (
  item.treatmentName || item.name || item.description || item.code || ''
)

export const getStep2Fields = (eligibilityData = []) => {
  const services = eligibilityData.filter((item) => item.active).map((item) => item.service_type)
  const fields = [...new Set(services.flatMap((service) => SERVICE_FIELDS[service] || []))]
  if (services.includes('Exam') && services.includes('Frames') && !services.includes('Spectacle Lens')) {
    return fields.filter((field) => field !== 'dilationPerformed')
  }
  return fields
}

export const getVisibleStepFields = (eligibilityData = [], activeModule = 'second') => {
  const fields = getStep2Fields(eligibilityData)
  const examFields = fields.filter((field) => field !== 'spectacle_lens')
  const hasSpectacle = fields.includes('spectacle_lens')

  if (activeModule === 'spectacle') return hasSpectacle ? ['spectacle_lens'] : []
  if (hasSpectacle && examFields.length) return examFields
  return fields
}

const parseCodeLabel = (value) => {
  if (!value) return null
  if (typeof value === 'object') return value.code ? value : null
  const [code, ...rest] = String(value).split(' - ')
  return { code: code.trim(), description: rest.join(' - ').trim() }
}

const EMPTY_LENSES = {
  perscriptions: {},
  len_information: {
    lens_design: {},
  },
  frame: {
    frame_not_in_catalog: true,
  },
}

export const getInitialVersantValues = (claimForm = {}) => {
  const codeSection = claimForm.exam || claimForm.frame || claimForm.contact_lens || claimForm.contact_lens_fit || {}
  const diagnosisFromClaim = (codeSection.diagnosis_code || []).map(parseCodeLabel).filter(Boolean)
  const procedureFromClaim = (codeSection.procedure_code || []).map(parseCodeLabel).filter(Boolean)
  const spectacle_lens = claimForm.spectacle_lens || {}
  const claimCharges = Array.isArray(claimForm.claim_charges) ? claimForm.claim_charges : []
  const hcpcsRows = claimCharges.map((charge = {}) => {
    const diagnosisCodes = (charge.diagnosis_codes || []).map((code) => {
      const parsed = typeof code === 'string' ? parseCodeLabel(code) : code
      return {
        ...(parsed || {}),
        code: parsed?.code || code,
        primary: (parsed?.code || code) === charge.primary_diagnosis,
      }
    }).filter((item) => item.code)

    return {
      code: charge.code || '',
      description: charge.description || '',
      modifier: charge.modifier || '',
      uc_charge: charge.charges ?? '0',
      units: charge.units || '1',
      authorization: charge.authorization || '',
      diagnosis_codes: diagnosisCodes,
    }
  })

  return {
    services: {
      diagnosisCodes: normalizeVersantCodes(
        claimForm.services?.diagnosisCodes || claimForm.diagnosis_codes || diagnosisFromClaim
      ),
      procedureCodes: normalizeVersantCodes(
        claimForm.services?.procedureCodes || claimForm.procedure_codes || procedureFromClaim
      ),
    },
    versant: {
      dilationPerformed: (claimForm.dilationPerformed ?? claimForm.versant?.dilationPerformed) !== false,
      isMemberDiabetic: !!(claimForm.isMemberDiabetic ?? claimForm.versant?.isMemberDiabetic),
      diabeticCodes: claimForm.diabeticCodes || claimForm.versant?.diabeticCodes || [DISEASE_NONE, ADDITIONAL_NONE],
      final: {
        ...(claimForm.versant?.final || {}),
        hcpcs_rows: claimForm.versant?.final?.hcpcs_rows || hcpcsRows,
      },
    },
    spectacle_lens: {
      ...EMPTY_LENSES,
      ...spectacle_lens,
      perscriptions: {
        ...EMPTY_LENSES.perscriptions,
        ...(spectacle_lens.perscriptions || {}),
      },
      len_information: {
        ...EMPTY_LENSES.len_information,
        ...(spectacle_lens.len_information || {}),
        lens_design: {
          ...EMPTY_LENSES.len_information.lens_design,
          ...(spectacle_lens.len_information?.lens_design || {}),
        },
      },
      frame: {
        ...EMPTY_LENSES.frame,
        ...(spectacle_lens.frame || {}),
      },
    },
  }
}

const CODE_SERVICE_KEYS = {
  Exam: 'exam',
  Frames: 'frame',
  'Contact Lens': 'contact_lens',
  'Contact Lens Fit & F/U': 'contact_lens_fit',
}

const getActiveServices = (eligibilityData = []) => (
  eligibilityData.filter((item) => item.active).map((item) => item.service_type)
)

const toDisplayCodeList = (codes) => (
  normalizeVersantCodes(codes).map(formatVersantCodeLabel).filter(Boolean)
)

const buildCodesSection = (services = {}) => ({
  diagnosis_code: toDisplayCodeList(services.diagnosisCodes),
  procedure_code: toDisplayCodeList(services.procedureCodes),
})

const valueOrEmpty = (value) => (value === undefined || value === null ? '' : value)

/** Pick spectacle_lens payload using the same keys as the form fields. */
export const buildLensesSection = (spectacle_lens = {}) => {
  const p = spectacle_lens.perscriptions || {}
  const len = spectacle_lens.len_information || {}
  const design = len.lens_design || {}
  const frame = spectacle_lens.frame || {}

  return {
    perscriptions: {
      right_sphere: valueOrEmpty(p.right_sphere),
      right_cyclinder: valueOrEmpty(p.right_cyclinder),
      right_axis: valueOrEmpty(p.right_axis),
      right_addition: valueOrEmpty(p.right_addition),
      left_sphere: valueOrEmpty(p.left_sphere),
      left_cyclinder: valueOrEmpty(p.left_cyclinder),
      left_axis: valueOrEmpty(p.left_axis),
      left_addition: valueOrEmpty(p.left_addition),
      right_prism_base_1: valueOrEmpty(p.right_prism_base_1),
      right_prism_value_1: valueOrEmpty(p.right_prism_value_1),
      right_prism_base_2: valueOrEmpty(p.right_prism_base_2),
      right_prism_value_2: valueOrEmpty(p.right_prism_value_2),
      left_prism_base_1: valueOrEmpty(p.left_prism_base_1),
      left_prism_value_1: valueOrEmpty(p.left_prism_value_1),
      left_prism_base_2: valueOrEmpty(p.left_prism_base_2),
      left_prism_value_2: valueOrEmpty(p.left_prism_value_2),
      right_distance_pd: valueOrEmpty(p.right_distance_pd),
      left_distance_pd: valueOrEmpty(p.left_distance_pd),
      right_near_pd: valueOrEmpty(p.right_near_pd),
      left_near_pd: valueOrEmpty(p.left_near_pd),
      right_height_pd: valueOrEmpty(p.right_height_pd),
      left_heighte_pd: valueOrEmpty(p.left_heighte_pd),
    },
    len_information: {
      lab: valueOrEmpty(len.lab),
      len_type: valueOrEmpty(len.len_type),
      job_type: valueOrEmpty(len.job_type),
      lens_design: {
        lens_material_name: valueOrEmpty(design.lens_material_name),
        lens_design: valueOrEmpty(design.lens_design),
        base: valueOrEmpty(design.base),
      },
    },
    frame: {
      manufacturer: valueOrEmpty(frame.manufacturer),
      brand: valueOrEmpty(frame.brand),
      model: valueOrEmpty(frame.model),
      color: valueOrEmpty(frame.color),
      eye_size: valueOrEmpty(frame.eye_size),
      temple_lenght: valueOrEmpty(frame.temple_lenght),
      frame_type: valueOrEmpty(frame.frame_type),
      thickenss_type: valueOrEmpty(frame.thickenss_type),
      left_thickness: valueOrEmpty(frame.left_thickness),
      right_thickness: valueOrEmpty(frame.right_thickness),
      a_box: valueOrEmpty(frame.a_box),
      b_box: valueOrEmpty(frame.b_box),
      dbl: valueOrEmpty(frame.dbl),
      ed: valueOrEmpty(frame.ed),
    },
  }
}

/** Build claim_charges from final-step HCPCS rows. */
export const buildClaimCharges = (data = {}) => {
  const rows = data.versant?.final?.hcpcs_rows || []

  return (Array.isArray(rows) ? rows : []).map((row = {}) => {
    const diagnosis = normalizeVersantCodes(row.diagnosis_codes)
    const primary = diagnosis.find((item) => item.primary) || diagnosis[0]

    return {
      code: row.code || '',
      description: row.description || '',
      charges: valueOrEmpty(row.uc_charge),
      diagnosis_codes: diagnosis.map((item) => item.code).filter(Boolean),
      primary_diagnosis: primary?.code || '',
    }
  })
}

/** Build selected service sections for claim_info root (no claim_data wrapper). */
export const buildVersantClaimData = (data = {}) => {
  const activeServices = getActiveServices(data.eligibility?.data)
  const claimInfoSections = {}

  activeServices.forEach((service) => {
    const key = CODE_SERVICE_KEYS[service]
    if (key) {
      claimInfoSections[key] = buildCodesSection(data.services)
      return
    }
    if (service === 'Spectacle Lens') {
      claimInfoSections.spectacle_lens = buildLensesSection(data.spectacle_lens)
    }
  })

  const claimCharges = buildClaimCharges(data)
  if (claimCharges.length) {
    claimInfoSections.claim_charges = claimCharges
  }

  return claimInfoSections
}

export const validateVersantClaimForm = (values, { editing = {} } = {}) => {
  const errors = {}
  const { activeModule } = editing
  const visibleFields = getVisibleStepFields(values.eligibility?.data || [], activeModule)
  const servicesErrors = {}

  if (['second', 'spectacle'].includes(activeModule)) {
    if (visibleFields.includes('diagnosisCodes') && !normalizeVersantCodes(values.services?.diagnosisCodes).length) {
      servicesErrors.diagnosisCodes = 'Required'
    }
    if (visibleFields.includes('procedureCodes') && !normalizeVersantCodes(values.services?.procedureCodes).length) {
      servicesErrors.procedureCodes = 'Required'
    }
  }

  if (activeModule === 'final') {
    if (!normalizeVersantCodes(values.services?.diagnosisCodes).length) servicesErrors.diagnosisCodes = 'Required'
    if (!normalizeVersantCodes(values.services?.procedureCodes).length) servicesErrors.procedureCodes = 'Required'
  }

  if (Object.keys(servicesErrors).length) errors.services = servicesErrors

  if (!['second', 'spectacle'].includes(activeModule) || !visibleFields.includes('spectacle_lens')) return errors

  const rx = values.spectacle_lens?.perscriptions || {}
  const sides = [
    ['right', 'right_sphere', 'right_cyclinder', 'right_axis', 'right_addition'],
    ['left', 'left_sphere', 'left_cyclinder', 'left_axis', 'left_addition'],
  ]

  sides.forEach(([, sphereKey, cylinderKey, axisKey, additionKey]) => {
    const sphere = rx[sphereKey]
    const cylinder = rx[cylinderKey]
    const axis = rx[axisKey]
    const addition = rx[additionKey]
    const needCylinderGroup = cylinder || cylinder === 0 || axis || axis === 0 || addition || addition === 0

    if (!sphere && sphere !== 0) {
      errors[`spectacle_lens.perscriptions.${sphereKey}`] = 'Required'
    }

    if (needCylinderGroup) {
      if (!cylinder && cylinder !== 0) errors[`spectacle_lens.perscriptions.${cylinderKey}`] = 'Required'
      if (!axis && axis !== 0) errors[`spectacle_lens.perscriptions.${axisKey}`] = 'Required'
      if (!addition && addition !== 0) errors[`spectacle_lens.perscriptions.${additionKey}`] = 'Required'
    }
  })

  return errors
}
